import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore'
import { db } from './firebase'

// Modern timeSlots-based availability interfaces
export interface TimeSlot {
  start: string
  end: string
}

export interface DayAvailability {
  enabled: boolean
  timeSlots: TimeSlot[]
}

export interface WeeklyAvailability {
  monday: DayAvailability
  tuesday: DayAvailability
  wednesday: DayAvailability
  thursday: DayAvailability
  friday: DayAvailability
  saturday: DayAvailability
  sunday: DayAvailability
}

// Legacy availability interface for backward compatibility
export interface LegacyDayAvailability {
  start: string
  end: string
  available: boolean
}

export interface LegacyWeeklyAvailability {
  monday: LegacyDayAvailability
  tuesday: LegacyDayAvailability
  wednesday: LegacyDayAvailability
  thursday: LegacyDayAvailability
  friday: LegacyDayAvailability
  saturday: LegacyDayAvailability
  sunday: LegacyDayAvailability
}

export interface Professional {
  uid: string
  displayName: string
  email: string
  userType: 'professional'
  specialization: string
  bio: string
  sessionRate: number
  experience: string
  skills: string[]
  profileImage: string
  rating: number
  totalReviews: number
  completedSessions: number
  availability: WeeklyAvailability
  stripeAccountId?: string
  isStripeOnboarded: boolean
  stripeAccountStatus?: {
    charges_enabled: boolean
    payouts_enabled: boolean
    currently_due: string[]
    eventually_due: string[]
    past_due: string[]
    pending_verification: string[]
  }
  createdAt: any
  updatedAt: any
  // Additional fields from database
  title?: string
  category?: string
  firstName?: string
  lastName?: string
  timezone?: string
  professionalTimezone?: string
  company?: string
  // Profile content from EditableProfessionalProfile
  summary?: string
  achievements?: string[]
  certifications?: string[]
}

/**
 * Converts legacy availability format to modern timeSlots format
 */
const convertLegacyAvailability = (availability: any): WeeklyAvailability => {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  const converted: any = {}

  for (const day of days) {
    const dayData = availability[day]

    if (!dayData) {
      // If no data, default to disabled
      converted[day] = { enabled: false, timeSlots: [] }
      continue
    }

    // Check if it's already in the new format
    if ('enabled' in dayData && 'timeSlots' in dayData) {
      converted[day] = dayData
      continue
    }

    // Convert from legacy format
    if ('available' in dayData && 'start' in dayData && 'end' in dayData) {
      converted[day] = {
        enabled: dayData.available,
        timeSlots: dayData.available && dayData.start && dayData.end
          ? [{ start: dayData.start, end: dayData.end }]
          : []
      }
    } else {
      // Fallback for unexpected format
      converted[day] = { enabled: false, timeSlots: [] }
    }
  }

  return converted as WeeklyAvailability
}

/**
 * Creates default modern availability for professionals
 * Returns empty availability - professionals must explicitly set their availability
 */
const getDefaultAvailability = (): WeeklyAvailability => ({
  monday: { enabled: false, timeSlots: [] },
  tuesday: { enabled: false, timeSlots: [] },
  wednesday: { enabled: false, timeSlots: [] },
  thursday: { enabled: false, timeSlots: [] },
  friday: { enabled: false, timeSlots: [] },
  saturday: { enabled: false, timeSlots: [] },
  sunday: { enabled: false, timeSlots: [] }
})

/**
 * Gets all professionals from Firestore
 */
export const getAllProfessionals = async (): Promise<Professional[]> => {
  try {
    const professionalsRef = collection(db, 'users')
    const q = query(
      professionalsRef,
      where('userType', '==', 'professional')
      // Note: orderBy('rating', 'desc') requires a composite index
      // Remove this until Firebase index is created, then sort in memory
    )

    const querySnapshot = await getDocs(q)
    const professionals: Professional[] = []

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      // Ensure required fields have defaults
      const professional: Professional = {
        uid: doc.id,
        displayName: (() => {
          if (data.firstName && data.lastName) {
            return `${data.firstName} ${data.lastName}`;
          }
          if (data.displayName && data.displayName !== data.email) {
            return data.displayName;
          }
          return 'Unknown Professional';
        })(),
        email: data.email || '',
        userType: 'professional',
        // Use actual specialization from database, not computed from other fields
        specialization: data.specialization &&
                       data.specialization !== 'Select your specialization' &&
                       data.specialization !== 'Select specialization' &&
                       data.specialization !== ''
          ? data.specialization
          : (data.category && data.category !== 'Select your category' ? data.category : 'Not specified'),
        bio: data.bio || 'Professional consultant with expertise in their field.',
        sessionRate: data.sessionRate || 30,
        experience: data.experience || '1+ years',
        skills: data.skills || [],
        profileImage: data.profileImage || '',
        rating: data.rating || 4.0,
        totalReviews: data.totalReviews || 0,
        completedSessions: data.completedSessions || 0,
        availability: data.availability ? convertLegacyAvailability(data.availability) : getDefaultAvailability(),
        stripeAccountId: data.stripeAccountId,
        isStripeOnboarded: data.isStripeOnboarded || false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        // Additional fields
        title: data.title,
        category: data.category,
        firstName: data.firstName,
        lastName: data.lastName,
        timezone: data.timezone,
        professionalTimezone: data.timezone,
        company: data.company,
        // Profile content from EditableProfessionalProfile
        summary: data.summary,
        achievements: data.achievements,
        certifications: data.certifications
      }
      professionals.push(professional)
    })

    // Sort by rating in memory (since we can't use orderBy without composite index)
    professionals.sort((a, b) => b.rating - a.rating)

    return professionals
  } catch (error) {
    console.error('Error fetching professionals:', error)
    throw new Error('Failed to fetch professionals')
  }
}

/**
 * Gets a single professional by ID
 */
export const getProfessionalById = async (professionalId: string): Promise<Professional | null> => {
  try {
    const professionalRef = doc(db, 'users', professionalId);
    const professionalDoc = await getDoc(professionalRef);

    if (!professionalDoc.exists()) {
      return null;
    }

    const data = professionalDoc.data();

    // Ensure user is a professional
    if (data.userType !== 'professional') {
      return null;
    }

    // Construct displayName with better fallback logic (same as getAllProfessionals)
    let displayName = (() => {
      if (data.firstName && data.lastName) {
        return `${data.firstName} ${data.lastName}`;
      }
      if (data.displayName && data.displayName !== data.email) {
        return data.displayName;
      }
      return 'Unknown Professional';
    })();

    return {
      uid: professionalDoc.id,
      email: data.email || '',
      displayName: displayName,
      profileImage: data.profileImage || '',
      userType: data.userType,
      bio: data.bio || '',
      specialization: data.specialization || 'General Consulting',
      experience: data.experience || '',
      sessionRate: data.sessionRate || 30,
      skills: data.skills || [],
      rating: data.rating || 4.0,
      totalReviews: data.totalReviews || 0,
      completedSessions: data.completedSessions || 0,
      availability: data.availability ? convertLegacyAvailability(data.availability) : getDefaultAvailability(),
      stripeAccountId: data.stripeAccountId,
      isStripeOnboarded: data.isStripeOnboarded || false,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      title: data.title,
      category: data.category,
      firstName: data.firstName,
      lastName: data.lastName,
      timezone: data.timezone,
      professionalTimezone: data.timezone,
      company: data.company,
      summary: data.summary,
      achievements: data.achievements,
      certifications: data.certifications
    };
  } catch (error) {
    console.error('Error fetching professional by ID:', error);
    return null;
  }
};

/**
 * Gets professionals by specialization
 */
export const getProfessionalsBySpecialization = async (
  specialization: string,
  limitCount: number = 10
): Promise<Professional[]> => {
  try {
    const professionalsRef = collection(db, 'users')
    const q = query(
      professionalsRef,
      where('userType', '==', 'professional'),
      where('specialization', '==', specialization),
      orderBy('rating', 'desc'),
      limit(limitCount)
    )

    const querySnapshot = await getDocs(q)
    const professionals: Professional[] = []

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      const professional: Professional = {
        uid: doc.id,
        displayName: (() => {
          if (data.firstName && data.lastName) {
            return `${data.firstName} ${data.lastName}`;
          }
          if (data.displayName && data.displayName !== data.email) {
            return data.displayName;
          }
          return 'Unknown Professional';
        })(),
        email: data.email || '',
        userType: 'professional',
        // Use actual specialization from database, not computed from other fields
        specialization: data.specialization &&
                       data.specialization !== 'Select your specialization' &&
                       data.specialization !== 'Select specialization' &&
                       data.specialization !== ''
          ? data.specialization
          : (data.category && data.category !== 'Select your category' ? data.category : 'Not specified'),
        bio: data.bio || 'Professional consultant with expertise in their field.',
        sessionRate: data.sessionRate || 30,
        experience: data.experience || '1+ years',
        skills: data.skills || [],
        profileImage: data.profileImage || '',
        rating: data.rating || 4.0,
        totalReviews: data.totalReviews || 0,
        completedSessions: data.completedSessions || 0,
        availability: data.availability ? convertLegacyAvailability(data.availability) : getDefaultAvailability(),
        stripeAccountId: data.stripeAccountId,
        isStripeOnboarded: data.isStripeOnboarded || false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        // Additional fields
        title: data.title,
        category: data.category,
        firstName: data.firstName,
        lastName: data.lastName,
        timezone: data.timezone,
        professionalTimezone: data.timezone,
        company: data.company,
        // Profile content from EditableProfessionalProfile
        summary: data.summary,
        achievements: data.achievements,
        certifications: data.certifications
      }
      professionals.push(professional)
    })

    return professionals
  } catch (error) {
    console.error('Error fetching professionals by specialization:', error)
    throw new Error('Failed to fetch professionals by specialization')
  }
}

/**
 * Gets top-rated professionals
 */
export const getTopRatedProfessionals = async (
  limitCount: number = 5
): Promise<Professional[]> => {
  try {
    const professionalsRef = collection(db, 'users')
    const q = query(
      professionalsRef,
      where('userType', '==', 'professional'),
      where('rating', '>=', 4.5),
      orderBy('rating', 'desc'),
      orderBy('totalReviews', 'desc'),
      limit(limitCount)
    )

    const querySnapshot = await getDocs(q)
    const professionals: Professional[] = []

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      const professional: Professional = {
        uid: doc.id,
        displayName: (() => {
          if (data.firstName && data.lastName) {
            return `${data.firstName} ${data.lastName}`;
          }
          if (data.displayName && data.displayName !== data.email) {
            return data.displayName;
          }
          return 'Unknown Professional';
        })(),
        email: data.email || '',
        userType: 'professional',
        // Use actual specialization from database, not computed from other fields
        specialization: data.specialization &&
                       data.specialization !== 'Select your specialization' &&
                       data.specialization !== 'Select specialization' &&
                       data.specialization !== ''
          ? data.specialization
          : (data.category && data.category !== 'Select your category' ? data.category : 'Not specified'),
        bio: data.bio || 'Professional consultant with expertise in their field.',
        sessionRate: data.sessionRate || 30,
        experience: data.experience || '1+ years',
        skills: data.skills || [],
        profileImage: data.profileImage || '',
        rating: data.rating || 4.0,
        totalReviews: data.totalReviews || 0,
        completedSessions: data.completedSessions || 0,
        availability: data.availability ? convertLegacyAvailability(data.availability) : getDefaultAvailability(),
        stripeAccountId: data.stripeAccountId,
        isStripeOnboarded: data.isStripeOnboarded || false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        // Additional fields
        title: data.title,
        category: data.category,
        firstName: data.firstName,
        lastName: data.lastName,
        timezone: data.timezone,
        professionalTimezone: data.timezone,
        company: data.company,
        // Profile content from EditableProfessionalProfile
        summary: data.summary,
        achievements: data.achievements,
        certifications: data.certifications
      }
      professionals.push(professional)
    })

    return professionals
  } catch (error) {
    console.error('Error fetching top-rated professionals:', error)
    throw new Error('Failed to fetch top-rated professionals')
  }
}

/**
 * Validates if a professional has a complete Stripe Connect account
 */
export const validateProfessionalStripeAccount = async (
  professionalId: string
): Promise<{ isValid: boolean; error?: string }> => {
  try {
    const { httpsCallable } = await import('firebase/functions')
    const { functions } = await import('./firebase')

    const validateAccountFn = httpsCallable(functions, 'validateStripeConnectAccount')
    const result = await validateAccountFn({ professionalId })

    const data = result.data as any
    return {
      isValid: data.isValid,
      error: data.error
    }
  } catch (error: any) {
    console.error('Error validating Stripe Connect account:', error)
    return {
      isValid: false,
      error: error.message || 'Failed to validate Stripe account'
    }
  }
}

/**
 * Checks if a professional is eligible for bookings (has valid Stripe Connect account)
 */
export const isProfessionalBookingEligible = (professional: Professional): boolean => {
  return professional.isStripeOnboarded && !!professional.stripeAccountId
}

/**
 * Manually refresh Stripe Connect account status for a professional
 */
export const refreshStripeConnectStatus = async (
  professionalId: string
): Promise<{ success: boolean; isStripeOnboarded?: boolean; message?: string }> => {
  try {
    const { httpsCallable } = await import('firebase/functions')
    const { functions } = await import('./firebase')

    const refreshStatusFn = httpsCallable(functions, 'refreshStripeConnectStatus')
    const result = await refreshStatusFn({ professionalId })

    const data = result.data as any
    return {
      success: data.success,
      isStripeOnboarded: data.isStripeOnboarded,
      message: data.message
    }
  } catch (error: any) {
    console.error('Error refreshing Stripe Connect status:', error)
    return {
      success: false,
      message: error.message || 'Failed to refresh Stripe status'
    }
  }
}

/**
 * Initiates Stripe Connect onboarding for a professional
 */
export const initiateStripeConnectOnboarding = async (
  professionalId: string,
  options?: { baseUrl?: string }
): Promise<{ url?: string; error?: string }> => {
  try {
    const { httpsCallable } = await import('firebase/functions')
    const { functions } = await import('./firebase')

    const createAccountLinkFn = httpsCallable(functions, 'createStripeConnectAccountLink')
    const result = await createAccountLinkFn({
      professionalId,
      baseUrl: options?.baseUrl
    })

    const data = result.data as any
    return {
      url: data.url,
      error: data.error
    }
  } catch (error: any) {
    console.error('Error initiating Stripe Connect onboarding:', error)
    return {
      error: error.message || 'Failed to initiate Stripe onboarding'
    }
  }
}

/**
 * Searches professionals by name, specialization, or skills
 */
export const searchProfessionals = async (
  searchTerm: string
): Promise<Professional[]> => {
  try {
    // Note: Firestore doesn't support full-text search natively
    // This is a basic implementation - consider using Algolia or similar for production
    const professionalsRef = collection(db, 'users')
    const q = query(
      professionalsRef,
      where('userType', '==', 'professional'),
      orderBy('rating', 'desc')
    )

    const querySnapshot = await getDocs(q)
    const professionals: Professional[] = []

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      const professional: Professional = {
        uid: doc.id,
        displayName: (() => {
          if (data.firstName && data.lastName) {
            return `${data.firstName} ${data.lastName}`;
          }
          if (data.displayName && data.displayName !== data.email) {
            return data.displayName;
          }
          return 'Unknown Professional';
        })(),
        email: data.email || '',
        userType: 'professional',
        // Use actual specialization from database, not computed from other fields
        specialization: data.specialization &&
                       data.specialization !== 'Select your specialization' &&
                       data.specialization !== 'Select specialization' &&
                       data.specialization !== ''
          ? data.specialization
          : (data.category && data.category !== 'Select your category' ? data.category : 'Not specified'),
        bio: data.bio || 'Professional consultant with expertise in their field.',
        sessionRate: data.sessionRate || 30,
        experience: data.experience || '1+ years',
        skills: data.skills || [],
        profileImage: data.profileImage || '',
        rating: data.rating || 4.0,
        totalReviews: data.totalReviews || 0,
        completedSessions: data.completedSessions || 0,
        availability: data.availability ? convertLegacyAvailability(data.availability) : getDefaultAvailability(),
        stripeAccountId: data.stripeAccountId,
        isStripeOnboarded: data.isStripeOnboarded || false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        // Additional fields
        title: data.title,
        category: data.category,
        firstName: data.firstName,
        lastName: data.lastName,
        timezone: data.timezone,
        professionalTimezone: data.timezone,
        company: data.company,
        // Profile content from EditableProfessionalProfile
        summary: data.summary,
        achievements: data.achievements,
        certifications: data.certifications
      }

      // Client-side filtering for search
      const searchLower = searchTerm.toLowerCase()
      const matchesName = professional.displayName?.toLowerCase().includes(searchLower)
      const matchesSpecialization = professional.specialization?.toLowerCase().includes(searchLower)
      const matchesSkills = professional.skills?.some(skill =>
        skill.toLowerCase().includes(searchLower)
      )
      const matchesBio = professional.bio?.toLowerCase().includes(searchLower)

      if (matchesName || matchesSpecialization || matchesSkills || matchesBio) {
        professionals.push(professional)
      }
    })

    return professionals
  } catch (error) {
    console.error('Error searching professionals:', error)
    throw new Error('Failed to search professionals')
  }
}